class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    // Bind immediately for initial markup.
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.openFromRedirectIfNeeded();
    // Some headers (especially overlay/mobile variants) can be re-rendered or replaced after
    // `cart-drawer.js` runs. Observe DOM mutations and (re)bind any new cart icons.
    if (this.headerCartIconObserver) return;

    this.headerCartIconObserver = new MutationObserver(() => {
      this.setHeaderCartIconAccessibility();
    });

    this.headerCartIconObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.headerCartIconObserver) {
      this.headerCartIconObserver.disconnect();
      this.headerCartIconObserver = null;
    }
  }
  openFromRedirectIfNeeded() {
    let shouldOpen = false;
    try {
      shouldOpen = sessionStorage.getItem('kk_open_cart_drawer_on_load') === '1';
      if (shouldOpen) sessionStorage.removeItem('kk_open_cart_drawer_on_load');
    } catch (error) {
      shouldOpen = false;
    }

    if (!shouldOpen) return;
    requestAnimationFrame(() => this.open());
  }

  setHeaderCartIconAccessibility() {
    // In addition to Dawn's default #cart-icon-bubble, this theme also renders cart icons
    // in overlay headers / floating icon containers.
    const cartUrl = window?.routes?.cart_url;
    const selectors = [
      '#cart-icon-bubble',
      '[id^="cart-icon-bubble-"]',

      // Explicit overlay-header hooks (more stable than classnames if markup changes).
      'a[data-mobile-header-cart]',
      'a[data-mobile-floating-cart]',

      // Class-based fallbacks.
      'a.overlay-header-cart-icon',
      'a.main-overlay-header-cart-icon',
      ...(cartUrl ? [`a[href="${cartUrl}"]`] : []),
    ];

    const cartLinks = Array.from(new Set(document.querySelectorAll(selectors.join(','))));
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      // Avoid double-binding if sections re-render.
      if (cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        // Preserve standard “open in new tab/window” behaviors.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Ensure the browser paints the hidden state before activating,
    // so the slide/fade transition always plays.
    this.classList.add('animate');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.clearRemovedLines();
  }

  // Lines removed while the drawer is open stay visible (greyed out) until it closes.
  clearRemovedLines() {
    if (!this.removedLines?.length) return;
    this.removedLines = [];

    // Wait for the slide-out so the greyed rows don't vanish mid-animation
    setTimeout(() => {
      if (this.classList.contains('active')) return;
      this.querySelectorAll('.cart-item--removed').forEach((row) => row.remove());
      if (!this.querySelector('.cart-item')) {
        this.classList.add('is-empty');
        this.querySelector('cart-drawer-items')?.classList.add('is-empty');
        this.querySelector('.drawer__inner-empty')?.removeAttribute('hidden');
      }
    }, 400);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-restore-line]');
      if (!button) return;
      event.preventDefault();
      this.restoreRemovedLine(button.closest('.cart-item--removed'));
    });
  }

  get drawer() {
    return document.querySelector('cart-drawer');
  }

  connectedCallback() {
    super.connectedCallback();
    // Every drawer re-render creates a new cart-drawer-items, so re-insert removed lines here
    this.renderRemovedLines();
  }

  updateQuantity(line, quantity, event, name, variantId) {
    if (quantity === 0) this.stashRemovedLine(line);
    return super.updateQuantity(line, quantity, event, name, variantId);
  }

  stashRemovedLine(line) {
    const drawer = this.drawer;
    const row = document.getElementById(`CartDrawer-Item-${line}`);
    if (!drawer || !row || !drawer.classList.contains('active')) return;

    const ghost = row.cloneNode(true);
    ghost.classList.add('cart-item--removed');
    ghost.removeAttribute('id');
    ghost.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    ghost.querySelectorAll('cart-remove-button, .quantity-popover__info-button, .quantity-popover__info').forEach((el) =>
      el.remove()
    );
    ghost.querySelectorAll('.loading__spinner').forEach((el) => el.classList.add('hidden'));

    const input = ghost.querySelector('.quantity__input');
    if (input) {
      // No name, so the ghost is never submitted with the cart form
      input.removeAttribute('name');
      input.removeAttribute('data-index');
      // quantity-input looks up the plus button by name when max is set; the ghost plus has no name
      input.removeAttribute('max');
      input.value = 0;
      input.setAttribute('value', 0);
      input.disabled = true;
    }
    const minus = ghost.querySelector('.quantity__button[name="minus"]');
    // Minus keeps its name (quantity-input queries it) but is disabled
    if (minus) minus.disabled = true;
    const plus = ghost.querySelector('.quantity__button[name="plus"]');
    if (plus) {
      // Without a name the quantity-input element ignores the click; we handle it instead
      plus.removeAttribute('name');
      plus.setAttribute('data-restore-line', '');
    }

    drawer.removedLines = (drawer.removedLines || []).filter((entry) => entry.key !== row.dataset.lineKey);
    drawer.removedLines.push({ key: row.dataset.lineKey, position: parseInt(line), ghost });
    drawer.removedLinesHead = row.closest('table')?.querySelector('thead')?.outerHTML;
  }

  renderRemovedLines() {
    const drawer = this.drawer;
    if (!drawer?.removedLines?.length) return;

    // Drop lines that are back in the cart (re-added elsewhere, or the removal failed)
    const liveKeys = new Set([...this.querySelectorAll('.cart-item[data-line-key]:not(.cart-item--removed)')].map((row) => row.dataset.lineKey));
    drawer.removedLines = drawer.removedLines.filter((entry) => !liveKeys.has(entry.key));
    if (!drawer.removedLines.length) return;

    let tbody = this.querySelector('.cart-items tbody');
    if (!tbody) {
      // Cart is empty: build the table so the removed lines still have somewhere to live
      const contents = this.querySelector('#CartDrawer-CartItems');
      if (!contents) return;
      contents.insertAdjacentHTML(
        'afterbegin',
        `<div class="drawer__cart-items-wrapper"><table class="cart-items" role="table">${
          drawer.removedLinesHead || ''
        }<tbody role="rowgroup"></tbody></table></div>`
      );
      tbody = contents.querySelector('tbody');
      drawer.classList.remove('is-empty');
      this.classList.remove('is-empty');
      drawer.querySelector('.drawer__inner-empty')?.setAttribute('hidden', '');
    }

    [...drawer.removedLines]
      .sort((a, b) => a.position - b.position)
      .forEach((entry) => {
        const ghost = entry.ghost.cloneNode(true);
        ghost.dataset.removedKey = entry.key;
        tbody.insertBefore(ghost, tbody.children[entry.position - 1] || null);
      });
  }

  restoreRemovedLine(row) {
    const drawer = this.drawer;
    if (!row || !drawer) return;

    const item = { id: parseInt(row.dataset.variantId), quantity: parseInt(row.dataset.quantity) || 1 };
    const properties = JSON.parse(row.dataset.properties || '{}');
    if (properties && Object.keys(properties).length) item.properties = properties;
    if (row.dataset.sellingPlan) item.selling_plan = parseInt(row.dataset.sellingPlan);

    row.classList.add('cart-item--restoring');
    const errorText = row.querySelector('.cart-item__error-text');
    if (errorText) errorText.textContent = '';

    const body = JSON.stringify({
      items: [item],
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          row.classList.remove('cart-item--restoring');
          if (errorText) errorText.textContent = state.description || window.cartStrings.error;
          return;
        }

        drawer.removedLines = (drawer.removedLines || []).filter((entry) => entry.key !== row.dataset.removedKey);

        const html = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
        const freshInner = html.querySelector('.drawer__inner');
        drawer.classList.remove('is-empty');
        if (freshInner) drawer.querySelector('.drawer__inner').innerHTML = freshInner.innerHTML;

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && state.sections['cart-icon-bubble']) {
          bubble.innerHTML = this.getSectionInnerHTML(state.sections['cart-icon-bubble']);
          const cartLink = bubble.querySelector('a');
          if (cartLink) delete cartLink.dataset.cartDrawerBound;
          drawer.setHeaderCartIconAccessibility();
        }

        return fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            document.querySelector('cart-drawer-items')?.updateCartIconBadge(cart.item_count);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart, variantId: item.id });
          });
      })
      .catch(() => {
        row.classList.remove('cart-item--restoring');
        if (errorText) errorText.textContent = window.cartStrings.error;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
